import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as PR from "../../prime-modules/index";
import AdminHeader from "../layout/admin-header";
import AdminFooter from "../layout/admin-footer";
import "./AdminUsers.scss";
import { getAdminsList, createAdmin, updateAdmin, deleteAdmin } from "../../services/api";
import { useFormik } from "formik";
import * as Yup from "yup";
import { PERMISSIONS, hasPermission } from "../../utils/permissions";

const AdminUsers = () => {
    const dispatch = useDispatch();
    const adminData = useSelector((state) => state.adminAuth.adminSessionData);
    const headers = useMemo(() => {
        return { sessionid: adminData.sessionId };
    }, [adminData.sessionId]);

    const [admins, setAdmins] = useState([]);
    const [loading, setLoading] = useState(false);
    const [adminDialog, setAdminDialog] = useState(false);
    const [deleteAdminDialog, setDeleteAdminDialog] = useState(false);
    const [selectedAdmin, setSelectedAdmin] = useState(null);
    const [searchKeywords, setSearchKeywords] = useState("");
    const [appliedSearchQuery, setAppliedSearchQuery] = useState("");
    const [searchRole, setSearchRole] = useState(0);
    const [appliedRole, setAppliedRole] = useState(0);
    const [showDeleted, setShowDeleted] = useState(false);
    const [filterOpen, setFilterOpen] = useState(false);

    const toggleFilter = () => setFilterOpen(!filterOpen);

    const filteredAdmins = useMemo(() => {
        let result = admins;

        if (!showDeleted) {
            result = result.filter(admin => !admin.isDeleted);
        }

        if (appliedSearchQuery) {
            const value = appliedSearchQuery.toLowerCase();
            result = result.filter(admin =>
                admin.userName.toLowerCase().includes(value) ||
                admin.email.toLowerCase().includes(value)
            );
        }

        if (appliedRole !== 0) {
            result = result.filter(admin => admin.role === appliedRole);
        }

        return result;
    }, [admins, appliedSearchQuery, appliedRole, showDeleted]);
    const toast = useRef(null);

    const roles = [
        { label: 'Super Admin', value: 1 }, // All Admin permissions plus the ability to manage BO user
        { label: 'Admin', value: 2 }, // All permissions, except managing other BO users
        { label: 'Finance', value: 3 }, // View and manage orders, view users, prices, promos, reports, and push notifications
        { label: 'Read Only', value: 4 }, // View orders, users, prices, promos, reports, and push notifications. No write access
        { label: 'Content Manager', value: 5 }, // View and manage translations and brand assets
        { label: 'Shipping Manager', value: 6 } // View orders and users, manage physical SIM shipping status
    ];
    const roleInfo = `1. Super Admin: \n All Admin permissions plus the ability to manage BO user\n\n` +
        `2. Admin:\n All permissions, except managing other BO users\n\n` +
        `3. Finance:\n View and manage orders, view users, prices, promos, reports, and push notifications\n\n` +
        `4. Read Only:\n View orders, users, prices, promos, reports, and push notifications. No write access\n\n` +
        `5. Content Manager:\n View and manage translations and brand assets\n\n` +
        `6. Shipping Manager:\n View orders and users, manage physical SIM shipping status`;

    const fetchAdmins = useCallback(() => {
        setLoading(true);
        getAdminsList(headers, dispatch, (response) => {
            if (response.result === "SUCCESS") {
                setAdmins(response.data);
            } else {
                toast.current.show({ severity: 'error', summary: 'Error', detail: 'Failed to fetch admins' });
            }
            setLoading(false);
        });
    }, [dispatch, headers]);

    const handleSearch = (e) => {
        setSearchKeywords(e.target.value);
    };

    const triggerSearch = () => {
        setAppliedSearchQuery(searchKeywords);
        setAppliedRole(searchRole);
    };

    const resetSearch = () => {
        setSearchKeywords("");
        setAppliedSearchQuery("");
        setSearchRole(0);
        setAppliedRole(0);
    };

    useEffect(() => {
        if (adminData.sessionId) {
            fetchAdmins();
        }
    }, [adminData.sessionId, fetchAdmins]);

    const formik = useFormik({
        initialValues: {
            userName: "",
            email: "",
            password: "",
            role: 1,
            isActive: true
        },
        validationSchema: Yup.object({
            userName: Yup.string().min(3, "Username must be at least 3 characters").required("Username is required"),
            email: Yup.string().email("Invalid email").required("Email is required"),
            password: Yup.string().min(8, "Password must be at least 8 characters").when('id', {
                is: (val) => !val,
                then: (schema) => schema.required("Password is required"),
                otherwise: (schema) => schema.notRequired()
            }),
            role: Yup.number().required("Role is required")
        }),
        onSubmit: (values) => {
            setLoading(true);
            const callback = (response) => {
                if (response.result === "SUCCESS") {
                    toast.current.show({ severity: 'success', summary: 'Successful', detail: `User ${selectedAdmin ? 'Updated' : 'Created'}`, life: 3000 });
                    setAdminDialog(false);
                    fetchAdmins();
                } else {
                    toast.current.show({ severity: 'error', summary: 'Error', detail: response.error?.errorMsg || 'Operation failed' });
                }
                setLoading(false);
            };

            if (selectedAdmin) {
                const filteredValues = Object.fromEntries(
                    Object.entries(values).filter(([_, value]) => value !== "")
                );
                updateAdmin(selectedAdmin.id, filteredValues, headers, dispatch, callback);
            } else {
                createAdmin(values, headers, dispatch, callback);
            }
        }
    });

    const openNew = () => {
        setSelectedAdmin(null);
        formik.resetForm({
            values: {
                userName: "",
                email: "",
                password: "",
                role: 1,
                isActive: true
            }
        });
        setAdminDialog(true);
    };

    const editAdmin = (admin) => {
        setSelectedAdmin(admin);
        formik.resetForm({
            values: {
                userName: admin.userName,
                email: admin.email,
                password: "", // Handled by BE if empty
                role: admin.role,
                isActive: admin.isActive === undefined ? true : admin.isActive,
                id: admin.id
            }
        });
        setAdminDialog(true);
    };

    const confirmDeleteAdmin = (admin) => {
        setSelectedAdmin(admin);
        setDeleteAdminDialog(true);
    };

    const deleteAdminAction = () => {
        setLoading(true);
        deleteAdmin(selectedAdmin.id, headers, dispatch, (response) => {
            if (response.result === "SUCCESS") {
                toast.current.show({ severity: 'success', summary: 'Successful', detail: 'User Deleted', life: 3000 });
                setDeleteAdminDialog(false);
                fetchAdmins();
            } else {
                toast.current.show({ severity: 'error', summary: 'Error', detail: 'Failed to delete admin' });
            }
            setLoading(false);
        });
    };

    const roleBodyTemplate = (rowData) => {
        const role = roles.find(r => r.value === rowData.role);
        return role ? role.label : rowData.role;
    };

    const actionBodyTemplate = (rowData) => {
        return (
            <React.Fragment>
                {hasPermission(adminData.permission, PERMISSIONS.MANAGE_ADMINS) && (
                    <PR.Button icon="pi pi-pencil" rounded outlined className="mr-2 icon-button" onClick={() => editAdmin(rowData)} />
                )}
                {hasPermission(adminData.permission, PERMISSIONS.MANAGE_ADMINS) && (
                    <PR.Button icon="pi pi-trash" rounded outlined severity="danger" className="icon-button delete" onClick={() => confirmDeleteAdmin(rowData)} disabled={rowData.isDeleted} />
                )}
            </React.Fragment>
        );
    };

    const itemTemplate = (rowData) => {
        return (
            <div className="col-12 custom-bg">
                <div className="custom-table-body">
                    <div className="table-grid">
                        <ul className="col-ul mobile-col-ul">
                            <li className="userName">
                                <div className="column-label">Username</div>
                                <div className={`column-data ${rowData.isDeleted ? 'deleted-user' : ''}`}>{rowData.userName}</div>
                            </li>
                            <li className="userEmail">
                                <div className="column-label">Email ID</div>
                                <div className={`column-data ${rowData.isDeleted ? 'deleted-user' : ''}`}>{rowData.email}</div>
                            </li>
                            <li className="role">
                                <div className="column-label">Role</div>
                                <div className={`column-data ${rowData.isDeleted ? 'deleted-user' : ''}`}>{roleBodyTemplate(rowData)}</div>
                            </li>
                            <li className="userStatus">
                                <div className="column-label">Status</div>
                                <div className="column-data">
                                    <span className={`status-badge ${rowData.isActive ? 'isActive' : 'isInactive'}`}>
                                        {rowData.isActive ? 'Active' : 'Inactive'}
                                    </span>
                                    {/* {rowData.isDeleted && <span className="text-red-500 font-bold ml-1">(Deleted)</span>} */}
                                </div>
                            </li>
                            <li className="actions-tab">
                                <div className="column-label">Actions</div>
                                <div className="column-data">{actionBodyTemplate(rowData)}</div>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="main">
            <div className="layout-sidebar">
                <AdminHeader />
            </div>
            <div className="layout-content-wrapper relative">
                <section className="admin-users-container">
                    <div className="grid grid-nogutter">
                        <div className="col-12">
                            <div className="heading-sec mb-4 mobile-header-flex">
                                <div className="flex align-items-center justify-content-between mobile-flex">
                                    <h1>BO Users</h1>
                                    <div className="flex align-items-center justify-content-between mobile-screen-width gap-3">
                                        <div className="right-section flex align-items-center gap-3">
                                            {hasPermission(adminData.permission, PERMISSIONS.MANAGE_ADMINS) && (
                                                <PR.Button label="Create BO User" icon="pi pi-plus" className="create-button" onClick={openNew} />
                                            )}
                                        </div>
                                        <PR.Button text className="filter-toggle-button" onClick={toggleFilter}>
                                            <i className={`pi ${filterOpen ? "pi-filter-slash" : "pi-filter-fill"}`}></i>
                                        </PR.Button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className={`filter-right mb-4 ${filterOpen ? "mobile-filter-section" : ""}`}>
                        <div className="flex align-items-center mobile-flex gap-2">
                            <div className="users-search-input">
                                <span>
                                    <PR.InputText placeholder="Search by Username or Email" value={searchKeywords} onChange={handleSearch} onKeyDown={(e) => { if (e.key === 'Enter') triggerSearch(); }} />
                                </span>
                            </div>
                            <div className="users-search-input">
                                <span>
                                    <PR.Dropdown
                                        value={searchRole}
                                        options={[{ label: 'All Roles', value: 0 }, ...roles]}
                                        onChange={(e) => setSearchRole(e.value)}
                                        placeholder="Filter by Role"
                                        className="w-full"
                                    />
                                </span>
                            </div>
                            <div className="users-search-input last-buttons-block">
                                <span className="flex align-items-center gap-3">
                                    <span className="flex gap-2">
                                        <PR.Button label="Search" className="searchBtn" onClick={triggerSearch} />
                                        <PR.Button label="Reset" className="resetBtn" onClick={resetSearch} />
                                    </span>&nbsp;
                                    <div className="flex align-items-center gap-2 show-deleted-toggle">
                                        <PR.Checkbox inputId="showDeleted" onChange={e => setShowDeleted(e.checked)} checked={showDeleted} />
                                        <label htmlFor="showDeleted" className="cursor-pointer font-semibold" style={{ fontSize: '14px', color: '#3a476a' }}>Show Deleted Users</label>
                                    </div>
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="users-data-table card">
                        <div className="card datatable-scroll">
                            <div className="custom-table">
                                <div className="custom-table-header">
                                    <div className="table-grid">
                                        <ul className="col-ul">
                                            <li className="userName"><span>Username</span></li>
                                            <li className="userEmail"><span>Email ID</span></li>
                                            <li className="role"><span>Role</span></li>
                                            <li className="userStatus"><span>Status</span></li>
                                            <li className="actions-tab"><span>Actions</span></li>
                                        </ul>
                                    </div>
                                </div>
                                <PR.DataView value={filteredAdmins} itemTemplate={itemTemplate} loading={loading} emptyMessage="No admins found." />
                            </div>
                        </div>
                    </div>

                    <PR.Dialog visible={adminDialog} className="create-admin-role-dialog" header={selectedAdmin ? "Edit BO User" : "Create BO User"} draggable={false} resizable={false} modal onHide={() => setAdminDialog(false)}>
                        <form onSubmit={formik.handleSubmit} autoComplete="off">
                            {/* Dummy inputs to deceive browser auto-fill */}
                            <input type="text" name="hidden_username" autoComplete="username" style={{ display: "none" }} />
                            <input type="password" name="hidden_password" autoComplete="new-password" style={{ display: "none" }} />
                            <div className="field mt-3">
                                <label htmlFor="role" className="flex align-items-center gap-2">
                                    Role
                                    <i
                                        className="pi pi-info-circle info-icon cursor-pointer"
                                        data-pr-tooltip={roleInfo}
                                        data-pr-position="bottom"
                                        style={{ color: '#3a476a' }}
                                    ></i>
                                </label>
                                <PR.Dropdown id="role" name="role" value={formik.values.role} options={roles} onChange={(e) => formik.setFieldValue('role', e.value)} placeholder="Select a Role" />
                                <PR.Tooltip target=".pi-info-circle" />
                            </div>
                            <div className="field">
                                <label htmlFor="userName">Username</label>
                                <PR.InputText
                                    id="userName"
                                    name="userName"
                                    value={formik.values.userName}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    autoComplete="new-password"
                                    className={formik.errors.userName && formik.touched.userName ? "p-invalid" : ""}
                                />
                                {formik.errors.userName && formik.touched.userName && (
                                    <small className="p-error">{formik.errors.userName}</small>
                                )}
                            </div>
                            <div className="field mt-3">
                                <label htmlFor="email">Email</label>
                                <PR.InputText
                                    id="email"
                                    name="email"
                                    type="email"
                                    value={formik.values.email}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    autoComplete="new-password"
                                    className={formik.errors.email && formik.touched.email ? "p-invalid" : ""}
                                />
                                {formik.errors.email && formik.touched.email && (
                                    <small className="p-error">{formik.errors.email}</small>
                                )}
                            </div>
                            <div className="field mt-3">
                                <label htmlFor="password">Password</label>
                                <PR.Password id="password" name="password" value={formik.values.password} onChange={formik.handleChange} toggleMask feedback={false} autoComplete="new-password" className={formik.errors.password && formik.touched.password ? 'p-invalid' : ''} />
                                {formik.errors.password && formik.touched.password && <small className="p-error">{formik.errors.password}</small>}
                            </div>
                            <div className="field mt-3">
                                <label className="mb-2 block">Status</label>
                                <div className="flex flex-wrap gap-3">
                                    <div className="flex align-items-center">
                                        <PR.RadioButton inputId="isActive1" name="isActive" value={true} onChange={(e) => formik.setFieldValue('isActive', e.value)} checked={formik.values.isActive === true} />
                                        <label htmlFor="isActive1" className="ml-2">Active</label>
                                    </div>
                                    <div className="flex align-items-center">
                                        <PR.RadioButton inputId="isActive2" name="isActive" value={false} onChange={(e) => formik.setFieldValue('isActive', e.value)} checked={formik.values.isActive === false} />
                                        <label htmlFor="isActive2" className="ml-2">Inactive</label>
                                    </div>
                                </div>
                            </div>
                            <div className="flex justify-content-end mt-4 buttons-sections">
                                <PR.Button label="Cancel" type="button" className="confirm-button reset-btn min-width" onClick={() => setAdminDialog(false)} />
                                <PR.Button label={selectedAdmin ? "Update" : "Create"} type="submit" className="confirm-button min-width" loading={loading} />
                            </div>
                        </form>
                    </PR.Dialog>

                    <PR.Dialog visible={deleteAdminDialog} header="Confirm" className="create-admin-role-dialog" draggable={false} resizable={false} modal onHide={() => setDeleteAdminDialog(false)}>
                        <div className="confirmation-content">
                            <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: '1.5rem' }} />
                            {selectedAdmin && (
                                <span>
                                    Are you sure you want to delete <b>{selectedAdmin.userName}</b>?
                                </span>
                            )}
                        </div>
                        <div className="flex justify-content-end mt-4 buttons-sections">
                            <PR.Button label="No" icon="pi pi-times" className="p-button-text confirm-button reset-btn" onClick={() => setDeleteAdminDialog(false)} />
                            <PR.Button label="Yes" icon="pi pi-check" severity="danger" className="confirm-button yes" onClick={deleteAdminAction} loading={loading} />
                        </div>
                    </PR.Dialog>
                </section>
                <AdminFooter />
            </div>
            <PR.Toast ref={toast} />
        </div>
    );
};

export default AdminUsers;
